import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, AlertCircle, ShieldOff } from 'lucide-react'
import { useI18n } from '../../i18n'
import { authAPI } from '../../services/api'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'

/**
 * TwoFactorResetLinkScreen — PROD-1271. The page the link in the 2FA reset e-mail opens.
 * Copy approved by Alex (PROD-1271): every twoFactorEmailReset.* key.
 *
 * ★★★ THE ROUTE IS A CONTRACT WITH orella-api. The API mails
 *   PATIENT_APP_BASE + PATIENT_2FA_EMAIL_RESET_APP_PATH + '?token=<jwt>', and the path today is
 *   /two-factor-reset. scripts/check-2fa-email-reset.mjs reads the constant out of the API
 *   repo's own git blob and compares it with the route in App.jsx.
 *
 * ★★★ OPENING THIS PAGE MUST NOT SPEND THE TOKEN. Mail scanners prefetch every link in a
 *   delivered message. There is no request of any kind in an effect in this file: the ONLY
 *   network call is authAPI.confirm2faEmailReset, inside the button handler.
 *
 * ★★★ THE TOKEN IS A CREDENTIAL. It is read once from ?token= into memory, and the mount effect
 *   removes it from the address bar with history.replaceState (so it is not in the history
 *   entry, a bookmark, a shared screenshot of the URL, or the Referer of a later request). The
 *   handler strips it again before the confirm call. It is never logged, never written to
 *   localStorage, sessionStorage or the store, and never put in an error string. There is no
 *   console call in this file. No session comes back from the confirm: the next step really is
 *   the sign-in screen.
 *
 * ★★ OUTSIDE EVERY GUARD. The route is public: the person holding the link is usually signed
 *   out (and on an iPhone, in Safari). App.jsx mounts it under AuthLayout only, beside
 *   /reset-password: not under SessionLockGuard (which would keep path + query for the unlock),
 *   not under ProtectedRoute or the onboarding gate.
 */

// Remove ?token= (and the rest of the query) from the address bar. Local only: contacts nothing.
const stripTokenFromAddressBar = () => {
  try {
    const { pathname, hash } = window.location
    if (window.location.search) window.history.replaceState(window.history.state, '', pathname + hash)
  } catch (_) { /* nothing to strip */ }
}

const readTokenOnce = () => {
  try {
    return (new URLSearchParams(window.location.search).get('token') || '').trim()
  } catch (_) { return '' }
}

export default function TwoFactorResetLinkScreen() {
  const navigate = useNavigate()
  const { t } = useI18n()

  // ★ READ, NOT SPENT. Held in component memory only.
  const [token] = useState(readTokenOnce)
  // 'confirm' | 'ok' | 'dead' | 'blocked' | 'unconfirmed' | 'unavailable'.
  //   A link with no token at all is 'dead' from the start.
  const [phase, setPhase] = useState(token ? 'confirm' : 'dead')
  const [blockedReason, setBlockedReason] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  // Synchronous in-flight flag: `busy` is state and stays stale inside one task, so two clicks
  //   in the same task would otherwise send two confirms (the 2nd answers 400 and shows "dead").
  const inFlight = useRef(false)

  // ★★★ THIS EFFECT ONLY EDITS THE ADDRESS BAR. IT MAKES NO REQUEST, AND NOTHING IN THIS FILE
  //   MAY EVER ADD ONE TO AN EFFECT (mail-scanner prefetch, see above).
  useEffect(() => {
    stripTokenFromAddressBar()
  }, [])

  const handleConfirm = async () => {
    if (inFlight.current || busy || !token) return
    inFlight.current = true
    stripTokenFromAddressBar()
    setBusy(true)
    setNotice('')
    try {
      await authAPI.confirm2faEmailReset(token)
      setPhase('ok')
    } catch (err) {
      const status = err && err.status
      if (status === 400) {
        setPhase('dead')
      } else if (status === 403) {
        setBlockedReason(err.reason === 'email_changed' ? err.reason : null)
        setPhase('blocked')
      } else if (status === 404) {
        setPhase('unavailable')
      } else if (status === 429) {
        setNotice(t('twoFactorEmailReset.rateLimited'))
      } else if (status === 503) {
        // Nothing was changed (the server says so for every 503): the link is still good.
        setNotice(t('twoFactorEmailReset.busy'))
      } else {
        // 500 result_unconfirmed, a timeout (408), a lost connection (0) or anything unknown:
        //   the reset may or may not have happened, and only a sign-in can tell.
        setPhase('unconfirmed')
      }
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const toSignIn = () => navigate('/signin', { replace: true })

  if (phase === 'ok') {
    return (
      <ResponsiveWrapper className="bg-white" bottomInset={false}>
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
              {t('twoFactorEmailReset.okTitle')}
            </h1>
            <p className="text-gray-600 text-center md:text-lg mb-8">
              {t('twoFactorEmailReset.okText')}
            </p>
          </div>
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button onClick={toSignIn} className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full">
              {t('twoFactorEmailReset.okButton')}
            </button>
          </BottomActionBar>
        </div>
      </ResponsiveWrapper>
    )
  }

  if (phase !== 'confirm') {
    const titleKey = {
      dead: 'twoFactorEmailReset.deadTitle',
      blocked: 'twoFactorEmailReset.blockedTitle',
      unconfirmed: 'twoFactorEmailReset.unconfirmedTitle',
      unavailable: 'twoFactorEmailReset.unavailableTitle',
    }[phase]
    const textKey = phase === 'blocked'
      ? (blockedReason === 'email_changed' ? 'twoFactorEmailReset.blockedEmailChanged'
        : 'twoFactorEmailReset.blockedOther')
      : {
        dead: 'twoFactorEmailReset.deadText',
        unconfirmed: 'twoFactorEmailReset.unconfirmedText',
        unavailable: 'twoFactorEmailReset.notAvailable',
      }[phase]
    return (
      <ResponsiveWrapper className="bg-white" bottomInset={false}>
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center">
            <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain mb-8" />
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">{t(titleKey)}</h1>
            <p className="text-gray-600 text-center md:text-lg mb-8">{t(textKey)}</p>
          </div>
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button onClick={toSignIn} className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full">
              {t('twoFactorEmailReset.backToSignIn')}
            </button>
          </BottomActionBar>
        </div>
      </ResponsiveWrapper>
    )
  }

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
        <div className="flex items-center gap-4 mb-6">
          <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain" />
        </div>
        <div className="flex-1 flex flex-col">
          <div className="w-16 h-16 bg-orella-50 rounded-full flex items-center justify-center mb-5">
            <ShieldOff className="w-8 h-8 text-orella-500" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-orella-500 mb-2">
            {t('twoFactorEmailReset.confirmTitle')}
          </h1>
          <p className="text-gray-600 mb-6 md:text-lg">
            {t('twoFactorEmailReset.confirmText')}
          </p>
          {notice && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {notice}
            </div>
          )}
        </div>
        <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
          <button onClick={handleConfirm} disabled={busy}
            className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50">
            {t('twoFactorEmailReset.confirmButton')}
          </button>
          <button onClick={toSignIn} className="w-full py-4 mt-3 bg-gray-200 text-gray-600 font-semibold rounded-full">
            {t('common.cancel')}
          </button>
        </BottomActionBar>
      </div>
    </ResponsiveWrapper>
  )
}
